import { pipToDecimal } from '@katanaperps/katana-perps-sdk';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { isCallException } from 'ethers';

import useEventCallback from '#/hooks/useEventCallback';
import { transformConfigurationFields } from '#/lib/ethereum/contracts/vaults/vaultsConfigContracts';
import {
  getExchangeContract,
  getVaultAggregatorContract,
  getVaultProviderContract,
  LoadVaultDepositorsResultStructOutput,
} from '#/lib/ethereum/contracts/vaults/vaultsContract';

import { minutes, seconds } from './utils';

/**
 * State aggregator contract address of the vault's provider, taken from the
 * vaults API response (`stateAggregator` field) - resolved by the caller,
 * eg. via `useVaultStateAggregatorAddress` (vaultsQueries) or from already
 * loaded vault data. Queries skip until it resolves.
 */
type StateAggregatorAddress = string | null | undefined;

export function useInvalidateAllOnChainVaultsQueries() {
  const queryClient = useQueryClient();
  const invalidate = useEventCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ['vaultsOnChain'],
    });
  });
  return invalidate;
}

export function useVaultOnChainConfigurationsQuery({
  managerWallet,
  stateAggregator,
}: {
  managerWallet: string;
  stateAggregator: StateAggregatorAddress;
}) {
  return useQuery({
    queryKey: [
      'vaultsOnChain',
      'configurations',
      managerWallet,
      stateAggregator,
    ],
    queryFn: !stateAggregator
      ? skipToken
      : () =>
          getVaultAggregatorContract(stateAggregator)
            .loadVaultConfigurations(managerWallet)
            .catch(error => {
              console.warn(error);
              throw error;
            })
            .then(response => response.map(transformConfigurationFields))
            .then(response => response.reverse()),
    staleTime: seconds(15),
    refetchInterval: minutes(5),
  });
}

export function useVaultOnChainPendingConfigurationQuery({
  managerWallet,
}: {
  managerWallet: string;
}) {
  return useQuery({
    queryKey: ['vaultsOnChain', 'pendingConfigurations', managerWallet],
    queryFn: () =>
      getVaultProviderContract()
        .vaultConfigurationUpgradesByManagerWallet(managerWallet)
        .then(response => ({
          blockTimestampThreshold: response.blockTimestampThreshold,
          exists: response.exists,
          vaultConfigurationFieldsTransformed: response.exists
            ? transformConfigurationFields(response.newVaultConfigurationFields)
            : undefined,
        })),
    staleTime: seconds(15),
    refetchInterval: minutes(5),
  });
}

export function useVaultOnChainBalanceForWalletQuery({
  enabled,
  managerWallet,
  depositorWallet,
}: {
  enabled?: boolean;
  managerWallet: string;
  depositorWallet: string;
}) {
  return useQuery({
    queryKey: [
      'vaultsOnChain',
      'loadVaultBalanceForWalletSummary',
      managerWallet,
      depositorWallet,
    ],
    // `enabled` must stay a separate option, not a skipToken condition - observers
    // share one query per key, so a skipToken from a disabled observer would remove
    // queryFn for all of them (causing "Missing queryFn" errors on invalidation).
    enabled,
    queryFn: async () => {
      const balance =
        await getVaultProviderContract().loadVaultBalanceForWalletSummary(
          managerWallet,
          depositorWallet
        );
      if (typeof balance?.owedQuantity === 'undefined') {
        // Basic runtime structure check
        throw new Error(
          'Incorrect loadVaultBalanceForWalletSummary response structure'
        );
      }
      return {
        costBasis: balance.costBasis,
        initialDepositTimestampInS: balance.initialDepositTimestampInS,
        lockedQuantity: balance.lockedQuantity,
        owedQuantity: balance.owedQuantity,
        pendingDepositQuantity: balance.pendingDepositQuantity,
        quantityAvailableToWithdraw: balance.quantityAvailableToWithdraw,
        withdrawalLimitWindowEndTimestampInS:
          balance.withdrawalLimitWindowEndTimestampInS,
      };
    },
    staleTime: seconds(7),
    refetchInterval: minutes(4),
  });
}

// export function useVaultOnChainConfigurationQuery({
//   managerWallet,
// }: {
//   managerWallet: string;
// }) {
//   return useQuery({
//     queryKey: ['vaultsOnChain', 'configuration', managerWallet],
//     queryFn: () => getVaultContract().loadVaultConfiguration(managerWallet, 2),
//     staleTime: seconds(15),
//     refetchInterval: minutes(5),
//   });
// }

// const fakeWithdrawalQueue = [
//   {
//     nonce: 24889031647993638872876733253896480994n,
//     depositorWallet: '0x742d35Cc6631C0532925a3b8D581C027d2b1c80C',
//     grossQuantity: 99996895700n,
//   },
//   {
//     nonce: 25411145238962640857750054050518678464n,
//     depositorWallet: '0x8ba1f109551bD432803012645Hac136c22C501e',
//     grossQuantity: 89996895700n,
//   },
//   {
//     nonce: 25975249756064202941970855221582487263n,
//     depositorWallet: '0x4B20993Bc481177ec7E8f571ceCaE8A9e22C02db',
//     grossQuantity: 89996895700n,
//   },
// ];

export function useVaultOnChainWithdrawalQueueQuery({
  enabled,
  managerWallet,
  stateAggregator,
  reduceInternalForProcessing,
}: {
  enabled?: boolean;
  managerWallet: string | undefined;
  stateAggregator: StateAggregatorAddress;
  reduceInternalForProcessing?: boolean;
}) {
  return useQuery({
    queryKey: [
      'vaultsOnChain',
      'withdrawalQueue',
      managerWallet,
      stateAggregator,
    ],
    // `enabled` must stay a separate option, not a skipToken condition (see
    // useVaultOnChainBalanceForWalletQuery)
    enabled,
    queryFn:
      !managerWallet || !stateAggregator
        ? skipToken
        : async () => {
            const contractResponse = await getVaultAggregatorContract(
              stateAggregator
            ).loadVaultWithdrawalQueue(managerWallet);
            return contractResponse.map(item => ({
              depositorWallet: item.depositorWallet,
              grossQuantity: item.grossQuantity,
              nonce: item.nonce,
            }));
          },
    staleTime: seconds(15),
    refetchInterval: data =>
      data.state.data &&
      data.state.data?.length > 0 &&
      reduceInternalForProcessing
        ? seconds(11)
        : minutes(1),
  });
}

export function useVaultOnChainDepositorsMapQuery({
  managerWallet,
  depositors,
  stateAggregator,
}: {
  managerWallet: string;
  depositors: string[] | undefined;
  stateAggregator: StateAggregatorAddress;
}) {
  // Filter out disconnected wallet - empty string
  const depositorsFiltered = depositors?.filter(depositor => !!depositor);
  return useQuery({
    queryKey: [
      'vaultsOnChain',
      'depositors',
      managerWallet,
      depositorsFiltered,
      stateAggregator,
    ],
    queryFn:
      !depositorsFiltered || depositorsFiltered.length === 0 || !stateAggregator
        ? skipToken
        : async () => {
            const responseList = await getVaultAggregatorContract(
              stateAggregator
            ).loadVaultDepositorsByManagerWallet(
              managerWallet,
              depositorsFiltered
            );
            return depositorsFiltered.reduce(
              (map, depositor, index) => {
                const depositorObj = responseList[index];
                map[depositor] = {
                  costBasis: depositorObj.costBasis,
                  initialDepositTimestampInS:
                    depositorObj.initialDepositTimestampInS,
                  owedQuantity: depositorObj.owedQuantity,
                };
                return map;
              },
              {} as {
                [depositor: string]: LoadVaultDepositorsResultStructOutput;
              }
            );
          },
    staleTime: seconds(15),
    refetchInterval: minutes(5),
  });
}

export type OnChainVaultDepositorsMap = {
  [managerWallet: string]: LoadVaultDepositorsResultStructOutput | undefined;
};

export function useVaultOnChainMulticallDepositorsMapQuery({
  managerVaults,
  depositor,
}: {
  /**
   * Manager wallet with the vault's `stateAggregator` (vaults API response) -
   * aggregators can differ per vault, so calls are grouped per contract
   */
  managerVaults:
    | { manager: string; stateAggregator: string | null }[]
    | undefined;
  depositor: string;
}) {
  return useQuery<OnChainVaultDepositorsMap>({
    queryKey: ['vaultsOnChain', 'depositorMulticall', managerVaults, depositor],
    // Skip also without a depositor (no wallet connected) - both skip
    // dependencies are part of the queryKey
    queryFn:
      !managerVaults || !depositor
        ? skipToken
        : async () => {
            // Vaults without an aggregator address are left out of the map
            const managersByAggregator = new Map<string, string[]>();
            for (const { manager, stateAggregator } of managerVaults) {
              if (stateAggregator) {
                const managers = managersByAggregator.get(stateAggregator);
                if (managers) {
                  managers.push(manager);
                } else {
                  managersByAggregator.set(stateAggregator, [manager]);
                }
              }
            }
            const map: OnChainVaultDepositorsMap = {};
            await Promise.all(
              [...managersByAggregator.entries()].map(
                async ([stateAggregator, managerWallets]) => {
                  const list = await getVaultAggregatorContract(
                    stateAggregator
                  ).loadVaultDepositorsByDepositorWallet(
                    managerWallets,
                    depositor
                  );
                  managerWallets.forEach((managerWallet, index) => {
                    const depositorObj = list[index];
                    map[managerWallet] = depositorObj
                      ? {
                          costBasis: depositorObj.costBasis,
                          initialDepositTimestampInS:
                            depositorObj.initialDepositTimestampInS,
                          owedQuantity: depositorObj.owedQuantity,
                        }
                      : undefined;
                  });
                }
              )
            );
            return map;
          },
    staleTime: seconds(15),
    refetchInterval: minutes(5),
  });
}

// export function useVaultOnChainDepositsQuery({ managerWallet }: { managerWallet: string; }) {
//   return useQuery({
//     queryKey: ['vaultOnchain', 'deposits', managerWallet],
//     queryFn: () => getAggregatorContract().loadVaultDeposits([managerWallet]),
//     staleTime: seconds(15),
//     refetchInterval: minutes(5),
//   });
// }

export function useVaultOnChainSummaryQuery({
  managerWallet,
}: {
  managerWallet: string;
}) {
  return useQuery({
    queryKey: ['vaultsOnChain', 'summary', managerWallet],
    queryFn: () =>
      getVaultProviderContract()
        .loadVaultSummary(managerWallet)
        .then(response => ({
          // Note: we cannot use spread as it would not be serializable
          didManagerWalletWithdrawExit: response.didManagerWalletWithdrawExit,
          exitedAccountValue: response.exitedAccountValue,
          exitedAtTimestampInS: response.exitedAtTimestampInS,
          exitedTotalOwedQuantity: response.exitedTotalOwedQuantity,
          isDepositEnabled: response.isDepositEnabled,
          isExited: response.isExited,
          isLiquidated: response.isLiquidated,
          managerLockedQuantity: response.managerLockedQuantity,
          depositorNetDeposits: response.depositorNetDeposits,
          numDepositorWallets: response.numDepositorWallets,
          totalLockedQuantity: response.totalLockedQuantity,
          totalOwedQuantity: response.totalOwedQuantity,
          // totalPendingDepositQuantity: response.totalPendingDepositQuantity,
          /** Transformed chain data with already converted to Decimal from PIP */
          configurationFieldsTransformed: transformConfigurationFields(
            response.configurationFields
          ),
        })),
    staleTime: seconds(15),
    refetchInterval: minutes(5),
  });
}

export function useVaultOnChainQuantityAvailableForExitWithdrawalQuery({
  enabled,
  managerWallet,
}: {
  enabled?: boolean;
  managerWallet: string | undefined;
}) {
  return useQuery({
    queryKey: [
      'vaultsOnChain',
      'quantityAvailableForExitWithdrawalData',
      managerWallet,
    ],
    // `enabled` must stay a separate option, not a skipToken condition (see
    // useVaultOnChainBalanceForWalletQuery)
    enabled,
    queryFn: !managerWallet
      ? skipToken
      : async () => {
          const quantityBN =
            await getExchangeContract().loadQuoteQuantityAvailableForExitWithdrawal(
              managerWallet
            );
          const quantityInDecimal = pipToDecimal(quantityBN);
          return { quantityInDecimal };
        },
    staleTime: seconds(15),
    refetchInterval: minutes(5),
  });
}

export function useVaultOnChainExitWalletSimulationQuery({
  enabled,
  managerWallet,
  depositorWallet,
}: {
  enabled?: boolean;
  managerWallet: string;
  /** Simulated msg.sender - only depositors may call exitWallet */
  depositorWallet: string | undefined;
}) {
  return useQuery({
    queryKey: [
      'vaultsOnChain',
      'exitWalletSimulation',
      managerWallet,
      depositorWallet,
    ],
    enabled,
    queryFn: !depositorWallet
      ? skipToken
      : async () => {
          try {
            // eth_call dry-run of the real exit transaction - success means
            // the contract would accept exitWallet from this depositor now
            await getVaultProviderContract().exitWallet.staticCall(
              managerWallet,
              { from: depositorWallet }
            );
            return true;
          } catch (error) {
            if (isCallException(error)) {
              console.log(
                '[vaultsOnChainQueries:ExitWalletSimulation] not eligible',
                error
              );
              // Contract revert (e.g. WalletCannotBeExited) - not eligible
              return false;
            }
            // RPC/network failure - keep the query in error state so
            // eligibility stays unknown instead of reading as "not eligible"
            throw error;
          }
        },
    staleTime: seconds(15),
    // The queue-age eligibility condition unlocks purely by time passing, so
    // re-simulate every minute to catch the flip
    refetchInterval: minutes(1),
  });
}

/**
 * Exchange-level exit status of the vault (manager) wallet. `exists` flips once
 * `exitWallet` is mined; the contracts accept Withdraw Exit only from
 * `effectiveTimestampInMs` on (exit time + chain propagation period).
 */
export function useVaultOnChainWalletExitStatusQuery({
  enabled,
  managerWallet,
}: {
  enabled?: boolean;
  managerWallet: string;
}) {
  return useQuery({
    queryKey: ['vaultsOnChain', 'walletExitStatus', managerWallet],
    enabled,
    queryFn: async () => {
      const exitStatus = await getExchangeContract().loadWalletExitStatus(
        managerWallet
      );
      return {
        exists: exitStatus.exists,
        effectiveTimestampInMs:
          Number(exitStatus.effectiveBlockTimestamp) * 1000,
      };
    },
    staleTime: seconds(15),
    // Availability unlocks purely by time passing
    refetchInterval: minutes(2),
  });
}
